import {
  QUIZ_DIFFICULTY,
  QUIZ_LIST_SECTIONS,
  QUIZ_STATE,
} from '@lib/constants/quizzes'
import { GradeType, TabType } from '@lib/types/common'
import {
  GenerateRandomRankOptionType,
  QuizLinkedEntityType,
  QuizStateType,
  RankObjType,
} from '@lib/types/quizzes'
import * as Sentry from '@sentry/node'
import { CClient, showErrorAlert } from '@common/utils'
import Router from 'next/router'
import {
  CompetititonsListItemType,
  CompetititonsListType,
} from '@lib/types/competitions'
import { getFromLocalStorage, setToLocalStorage } from './local-storage'

/**
 * If the playCount is less than 1000 then directly show the count
 * Else divide the count by 1000, round it and show it with thousand's unit (k).
 */
export const getFormattedPlayCount = (playCount: number) =>
  playCount < 1000
    ? playCount.toString()
    : `${(playCount / 1000).toFixed(0).toString()}k`

export const getGradeRange = (minGrade: GradeType, maxGrade: GradeType) =>
  `${minGrade.substr(5)} - ${maxGrade.substr(5)}`

export const getQuizState = (
  totalProblems: number,
  remainingProblems: number
): QuizStateType =>
  remainingProblems === 0
    ? QUIZ_STATE.COMPLETED
    : remainingProblems === totalProblems
    ? QUIZ_STATE.NOT_STARTED
    : QUIZ_STATE.INCOMPLETE

/**  Creates a new quiz participation */
export const onQuizPlay = async (quizId: number, quizSlug: string) => {
  let isErrorOccured = true
  try {
    const response = await CClient(
      `/api/v1/competitions/${quizSlug}/quiz-participations/`,
      {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          quiz: quizId,
        }),
      }
    )
    if (!response.ok) throw new Error(response.statusText)
    Router.push(`/quizzes/${quizSlug}/arena/`)
    isErrorOccured = false
    return isErrorOccured
  } catch (error) {
    Sentry.captureException(error)
    showErrorAlert({
      text: 'Something went wrong. Please reload the page and try again.',
    })
    return isErrorOccured
  }
}

/** Sort function that sorts quizzes according to difficulty:
 * [Beginner, Intermediate, Expert]
 */
export const getSortedQuiz = (
  quizA: CompetititonsListItemType<QuizLinkedEntityType>,
  quizB: CompetititonsListItemType<QuizLinkedEntityType>
) => {
  const { difficulty: difficultyA } = quizA.phases[0].linkedEntity
  const { difficulty: difficultyB } = quizB.phases[0].linkedEntity

  // Swap items only if first one is EXPERT OR second one is BEGINNER
  if (
    difficultyA === QUIZ_DIFFICULTY.EXPERT ||
    difficultyB === QUIZ_DIFFICULTY.BEGINNER
  )
    return 1

  return -1
}

/**  Redirects user to registration page where user can signup. On verify OTP step the quiz progress is saved. */
export const onCreateAccountToSave = () => {
  const quizSlug = Router.query.quizName
  Router.push(`/quizzes/${quizSlug}/register`)
}

/**
 *
 * @param generateRandomRankOptions
 * @returns A rank obj if it is stored in localstorage else generates new rank obj, stores it in localstorage and returns it.
 */
export const generateRandomRank = ({
  playCount,
  quizId,
  userId,
}: GenerateRandomRankOptionType) => {
  const localStorageKey = `quiz-rank-${quizId}-${userId}`
  const rankStoredInLocalStorage = getFromLocalStorage(localStorageKey)
  if (rankStoredInLocalStorage) {
    return JSON.parse(rankStoredInLocalStorage) as RankObjType
  }

  // 10% of play count or 10, whichever is higher
  const maxUserRank = Math.max(parseInt((playCount * 0.1).toFixed()), 10)
  // A random number between 1 and maxUserRank
  const userRank = parseInt((1 + Math.random() * maxUserRank).toFixed())
  const totalParticipants = Math.max(10, playCount)
  const rankObj: RankObjType = {
    userRank,
    totalParticipants,
  }
  setToLocalStorage(localStorageKey, JSON.stringify(rankObj), () =>
    console.error(
      `Failed to store to localstorage with key: ${localStorageKey}`
    )
  )
  return rankObj
}

export const getQuizSectionTabs = (
  quizzes: CompetititonsListType<QuizLinkedEntityType>
) => {
  const tabs: TabType[] = []

  if (quizzes.length > 0)
    tabs.push({
      label: 'All practice quizzes',
      value: QUIZ_LIST_SECTIONS.PRACTICE,
    })

  tabs.push({ label: 'Leaderboard', value: QUIZ_LIST_SECTIONS.LEADERBOARD })

  return tabs
}
